// The design-token schema. A design direction is a TokenSet (JSON) that fills it; a company's
// brand (colours, a curated font) is applied on top by the theme generator. This file decides the
// shape only, never a visual style.

import { z } from 'zod';
import { isHex } from './color.ts';

/** Colour roles every surface is styled from. "on*" roles are text/icons drawn on the matching role. */
export const COLOR_ROLES = [
  'bg',
  'surface',
  'surfaceRaised',
  'surfaceSunken',
  'text',
  'textMuted',
  'border',
  'borderStrong',
  'focusRing',
  'link',
  'primary',
  'primaryHover',
  'primaryPressed',
  'onPrimary',
  'accent',
  'onAccent',
  'success',
  'onSuccess',
  'warning',
  'onWarning',
  'danger',
  'onDanger',
  'info',
  'onInfo',
  'overlay',
] as const;
export type ColorRole = (typeof COLOR_ROLES)[number];

export const BRAND_REFS = [
  'brand.primary',
  'brand.primaryHover',
  'brand.primaryPressed',
  'brand.onPrimary',
  'brand.accent',
  'brand.onAccent',
] as const;
export type BrandRef = (typeof BRAND_REFS)[number];

export type ParsedRef =
  | { kind: 'hex'; hex: string; alpha?: number }
  | { kind: 'ref'; path: string; alpha?: number }
  | { kind: 'mix'; a: ParsedRef; b: ParsedRef; weight: number };

const SIMPLE_RE =
  /^(#[0-9A-Fa-f]{6}(?:[0-9A-Fa-f]{2})?|[a-z][a-zA-Z0-9]*\.[a-zA-Z0-9-]+)(?:\/(0(?:\.\d+)?|1(?:\.0+)?))?$/;
const MIX_RE = /^mix\(\s*([^,\s]+)\s*,\s*([^,\s]+)\s*,\s*(0(?:\.\d+)?|1(?:\.0+)?)\s*\)$/;

/**
 * Parses a colour reference: '#RRGGBB[AA]', a palette step ('neutral.50'), a brand slot
 * ('brand.primary'), optionally with an alpha suffix ('neutral.950/0.4'); or an sRGB mix of two of
 * those like CSS color-mix: 'mix(brand.primary, light.surface, 0.13)' is 13 % brand.
 */
export function parseColorRef(ref: string): ParsedRef | null {
  const m = MIX_RE.exec(ref);
  if (m) {
    const a = parseColorRef(m[1]!);
    const b = parseColorRef(m[2]!);
    return a && b && a.kind !== 'mix' && b.kind !== 'mix'
      ? { kind: 'mix', a, b, weight: Number(m[3]) }
      : null;
  }
  const s = SIMPLE_RE.exec(ref);
  if (!s) return null;
  const alpha = s[2] === undefined ? {} : { alpha: Number(s[2]) };
  return s[1]!.startsWith('#')
    ? { kind: 'hex', hex: s[1]!.toUpperCase(), ...alpha }
    : { kind: 'ref', path: s[1]!, ...alpha };
}

export const ColorRef = z
  .string()
  .refine(
    (r) => parseColorRef(r) !== null,
    'a #hex, ramp.step or brand.slot (optionally /alpha), or mix(a, b, weightOfA)',
  );

/** A role value can differ by audience: customer-facing surfaces carry full branding, staff apps accents only (§9). */
export const RoleValue = z.union([ColorRef, z.strictObject({ customer: ColorRef, staff: ColorRef })]);

const Hex = z.string().refine(isHex, 'a #RRGGBB or #RRGGBBAA colour');
const Px = z.number().min(0).max(9999); // 9999 for "fully rounded" radii
/** One radius, or four corners (top-left, top-right, bottom-right, bottom-left) for organic shapes. */
const Radius = z.union([Px, z.tuple([Px, Px, Px, Px])]);
const Bezier = z.tuple([z.number(), z.number(), z.number(), z.number()]);

/** The required roles, plus any extra named roles a design direction needs (e.g. 'water', 'okSoft'). */
const RoleMap = z
  .strictObject(
    Object.fromEntries(COLOR_ROLES.map((r) => [r, RoleValue])) as Record<ColorRole, typeof RoleValue>,
  )
  .catchall(RoleValue);

/** An extra WCAG pair a direction relies on (e.g. chip text on its soft fill), between any two roles. */
const ExtraContrast = z.strictObject({
  fg: z.string(),
  bg: z.string(),
  min: z.number().min(1).max(21),
  why: z.string().min(1),
});

const TypeStyle = z.strictObject({
  family: z.enum(['display', 'body', 'mono']),
  size: z.number().min(8).max(160),
  lineHeight: z.number().min(0.8).max(3),
  weight: z.int().min(100).max(900),
  letterSpacing: z.number().min(-0.2).max(0.5).optional(), // em
  tabularNums: z.boolean().optional(),
});

/**
 * A metric-matched local stand-in for a web font, used while it loads so the first frame wraps and
 * sits like the loaded one (no jump when the font arrives). Percentages, as CSS @font-face takes them.
 */
const FallbackFace = z.strictObject({
  family: z.string().min(1),
  local: z.array(z.string().min(1)).min(1),
  sizeAdjust: z.number().min(50).max(150),
  ascentOverride: z.number().min(0).max(200),
  descentOverride: z.number().min(0).max(200),
  lineGapOverride: z.number().min(0).max(200),
});

const ShadowLayer = z.strictObject({
  x: z.number(),
  y: z.number(),
  blur: Px,
  spread: z.number(),
  color: ColorRef,
});
/** Named shadows ('sh-stone', 'ring-now'); the same names in both modes. */
const ShadowSet = z.record(z.string().regex(/^[a-z][a-zA-Z0-9-]*$/), z.array(ShadowLayer));

const LightnessDelta = z.number().min(-0.3).max(0.3);

/** A token's own name, as it is written in CSS after its group's prefix ('control', 'btn-h'). */
const TokenName = z.string().regex(/^[a-z][a-z0-9-]*$/);
/**
 * A meaning or component token's value (system.ts): CSS text whose references are written in braces,
 * as the W3C Design Tokens format writes aliases. '{size.control}' is a base token ('size', 'space',
 * 'fs', 'fw', 'lh', 'w', 'layer', 'color', 'radius'); a bare '{control-h}' is a meaning token, or a
 * component token. '{w.rule} solid {color.bp-stroke}' composes them. generateTheme checks that every
 * reference resolves, with no cycle.
 */
const Alias = z.string().min(1).max(400);

export const TokenSet = z.strictObject({
  name: z.string().min(1),
  version: z.string().min(1),
  /** Named ramps of hex colours. Steps can be numbers ('500') or names ('ground'). */
  palette: z
    .record(z.string().regex(/^[a-z][a-zA-Z0-9]*$/), z.record(z.string().regex(/^[a-zA-Z0-9-]+$/), Hex))
    .refine((p) => Object.keys(p).length > 0, 'palette needs at least one ramp'),
  /** Candidates for automatic "on" colours over brand fills, e.g. a near-white and a near-black. */
  onColorCandidates: z.array(ColorRef).min(2),
  color: z.strictObject({ light: RoleMap, dark: RoleMap }),
  contrast: z.array(ExtraContrast).default([]),
  /** OKLCH lightness shifts used to derive brand hover/pressed states per mode. */
  brandDerive: z.strictObject({
    light: z.strictObject({ hover: LightnessDelta, pressed: LightnessDelta }),
    dark: z.strictObject({ hover: LightnessDelta, pressed: LightnessDelta }),
  }),
  typography: z.strictObject({
    families: z.strictObject({
      body: z.string().min(1),
      display: z.string().min(1),
      mono: z.string().min(1),
    }),
    /** Which family slots a company's curated font replaces on customer-facing surfaces (empty: none). */
    brandFontSlots: z.array(z.enum(['display', 'body'])),
    allowedBrandFonts: z.array(z.string().min(1)),
    /** Metric-matched stand-ins named in the families (e.g. "Gabarito Fallback"); emitted as @font-face. */
    fallbackFaces: z.array(FallbackFace).default([]),
    scale: z
      .strictObject({
        h1: TypeStyle,
        h2: TypeStyle,
        h3: TypeStyle,
        body: TypeStyle,
        bodySm: TypeStyle,
        label: TypeStyle,
        caption: TypeStyle,
      })
      .catchall(TypeStyle),
  }),
  /** Named radii ('r-stone', 'r-pebble-a'); a value is one radius or four corners. */
  radii: z.record(z.string().regex(/^[a-z][a-zA-Z0-9-]*$/), Radius),
  /** Which radius each kind of shape uses. */
  shape: z
    .strictObject({ control: z.string(), card: z.string(), chip: z.string(), sheet: z.string() })
    .catchall(z.string()),
  shadows: z.strictObject({ light: ShadowSet, dark: ShadowSet }),
  /** Which shadow means "at rest" and which "floating above the page" (menus, toasts, dragged items). */
  elevation: z.strictObject({ resting: z.string(), floating: z.string() }).catchall(z.string()),
  spacing: z
    .record(z.string().regex(/^[0-9]+(\.[0-9]+)?$|^px$/), Px)
    .refine(
      (s) => ['0', '1', '2', '3', '4', '6', '8'].every((k) => k in s),
      'spacing needs steps 0,1,2,3,4,6,8',
    ),
  /** Minimum touch target and the large primary-action height (iPad), in px. */
  touch: z
    .strictObject({ minTarget: z.number().min(24).max(96), primaryTarget: z.number().min(24).max(120) })
    .optional(),
  motion: z.strictObject({
    durations: z.strictObject({
      instant: z.int().min(0),
      fast: z.int().min(0),
      normal: z.int().min(0),
      slow: z.int().min(0),
    }),
    easings: z.strictObject({ standard: Bezier, enter: Bezier, exit: Bezier }),
  }),
  // The three layers of a design system (system.ts): base scales, meaning tokens that point at them, and
  // component tokens that point at meaning. All optional, so a direction without them (Riverstone) is
  // unchanged. A raw number lives here only: the CSS and the DTCG export are generated from it.
  /** Base: font weights ('strong': 600) → --fw-*. */
  weights: z.record(TokenName, z.int().min(100).max(900)).optional(),
  /** Base: line heights ('snug': 1.25) → --lh-*. */
  leading: z.record(TokenName, z.number().min(0.8).max(3)).optional(),
  /** Base: control and glyph sizes, in px ('control': 34) → --size-*. */
  sizes: z.record(TokenName, Px).optional(),
  /** Base: stroke widths, in px, per mode (the rule is 1.25 on the light sheet, 1 on the dark) → --w-*. */
  strokes: z.strictObject({ light: z.record(TokenName, Px), dark: z.record(TokenName, Px) }).optional(),
  /** Base: each layer's cast offset, in px ('panel': 6) → --layer-*-offset. */
  layers: z.record(TokenName, Px).optional(),
  /** Meaning: what a value is for ('control-h': '{size.control}'). */
  meaning: z.record(TokenName, Alias).optional(),
  /** Component: what each component reads ('btn-h': '{control-h}'); a px number is allowed only here. */
  components: z.record(TokenName, z.union([Alias, Px])).optional(),
});
/** What a design direction writes (contrast may be omitted). */
export type TokenSet = z.input<typeof TokenSet>;
export type ParsedTokenSet = z.output<typeof TokenSet>;

/** A company's brand, from settings (branding module). Colours are opaque. */
export const Brand = z.strictObject({
  primary: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'an opaque #RRGGBB colour'),
  accent: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  font: z.string().min(1).optional(),
});
export type Brand = z.infer<typeof Brand>;

export type Audience = 'customer' | 'staff';
